import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Pencil, Trash2, Tag, ChevronRight, Search, X, Settings2, GripVertical, Merge } from 'lucide-react';
import { toast } from 'sonner';
import { SidePanel } from '../components/SidePanel';
import * as api from '../api';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const TYPES = [
  { value: 'materiel',       label: 'Matériel',       bg: 'var(--primary-50)', color: 'var(--primary-light)' },
  { value: 'piece_detachee', label: 'Pièce détachée', bg: '#FFF7ED',           color: '#C2410C' },
  { value: 'main_oeuvre',    label: "Main d'œuvre",   bg: '#F5F3FF',           color: '#7C3AED' },
  { value: 'prestation',     label: 'Prestation',     bg: '#F0FDF4',           color: '#15803D' },
  { value: 'location',       label: 'Location',       bg: '#F0F9FF',           color: '#0369A1' },
];

const EMPTY_F  = { nom: '', type: 'materiel', compte_charge: '', compte_produit: '', is_assurance: false, tva_vente_type_id: '', tva_achat_type_id: '' };
const EMPTY_SF = { nom: '', famille_id: '', compte_charge: '', compte_produit: '', tva_vente_type_id: '', tva_achat_type_id: '' };

// ─── Item draggable d'une caractéristique ─────────────────────────────────────
function SortableCaracItem({ carac, onRemove, onUniteChange }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: carac.carac_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={{ ...style, borderColor: 'var(--border)', background: 'var(--primary-50)' }}
      className="flex items-center gap-2 px-3 py-2 rounded-lg border">
      {/* Poignée */}
      <button type="button" {...attributes} {...listeners}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-white/60"
        style={{ color: 'var(--text-disabled)', touchAction: 'none' }}>
        <GripVertical size={14} />
      </button>
      <span className="flex-1 text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{carac.nom}</span>
      {carac.type === 'numeric' && carac.unites?.length > 0 && (
        <select value={carac.unite_defaut || ''} onChange={e => onUniteChange(carac.carac_id, e.target.value)}
          className="text-xs px-2 py-1 rounded-lg border"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          <option value="">Unité déf.</option>
          {carac.unites.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      )}
      <button onClick={() => onRemove(carac.carac_id)}
        className="p-1 rounded hover:bg-red-50 flex-shrink-0" style={{ color: '#DC2626' }}>
        <X size={12} />
      </button>
    </div>
  );
}

// ─── Mini-composant : section carac dans le panneau sous-famille ───────────
function SousFamilleCaracsSection({ sfId, sfCaracs, onSaved }) {
  const [library, setLibrary] = useState([]);
  const [search, setSearch] = useState('');
  const [focused, setFocused] = useState(false);
  const parseCaracsArr = (v) => { if (!v) return []; if (Array.isArray(v)) return v; try { return JSON.parse(v); } catch { return []; } };
  const [localCaracs, setLocalCaracs] = useState(() => parseCaracsArr(sfCaracs));
  const [saving, setSaving] = useState(false);
  const dropdownRef = useRef(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    api.getCaracTechniques().then(r => setLibrary(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { setLocalCaracs(parseCaracsArr(sfCaracs)); }, [sfCaracs]);

  const isAssigned = (carac_id) => localCaracs.some(c => c.carac_id === carac_id);

  const toggle = (carac) => {
    if (isAssigned(carac.carac_id)) {
      setLocalCaracs(prev => prev.filter(c => c.carac_id !== carac.carac_id));
    } else {
      setLocalCaracs(prev => [...prev, { carac_id: carac.carac_id, unite_defaut: carac.unites?.[0] || null }]);
    }
  };

  const setUniteDefaut = (carac_id, unite) => {
    setLocalCaracs(prev => prev.map(c => c.carac_id === carac_id ? { ...c, unite_defaut: unite } : c));
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setLocalCaracs(prev => {
      const oldIdx = prev.findIndex(c => c.carac_id === active.id);
      const newIdx = prev.findIndex(c => c.carac_id === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const save = async () => {
    if (!sfId) return;
    setSaving(true);
    try {
      await api.updateSousFamilleCaracs(sfId, localCaracs);
      toast.success('Caractéristiques mises à jour');
      if (onSaved) onSaved(localCaracs);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const filtered = library.filter(c => c.nom.toLowerCase().includes(search.toLowerCase()));

  const assigned = localCaracs.map(lc => {
    const def = library.find(l => l.carac_id === lc.carac_id);
    return def ? { ...def, unite_defaut: lc.unite_defaut } : null;
  }).filter(Boolean);

  return (
    <div className="space-y-3 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="flex items-center gap-2">
        <Settings2 size={13} style={{ color: 'var(--primary-solid)' }} />
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          Caractéristiques techniques
        </span>
      </div>

      {/* Liste triable */}
      {assigned.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={assigned.map(c => c.carac_id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5">
              {assigned.map(c => (
                <SortableCaracItem
                  key={c.carac_id}
                  carac={c}
                  onRemove={(id) => setLocalCaracs(prev => prev.filter(c => c.carac_id !== id))}
                  onUniteChange={setUniteDefaut}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Recherche pour ajouter */}
      <div className="relative" ref={dropdownRef}>
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
        <input value={search} onChange={e => setSearch(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder="Rechercher une caractéristique..."
          className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm focus:outline-none"
          style={{ borderColor: focused ? 'var(--primary-solid)' : 'var(--border)' }} />
      </div>

      {(focused || search) && (
        <div className="rounded-lg border overflow-hidden max-h-40 overflow-y-auto"
          style={{ borderColor: 'var(--border)' }}>
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-xs text-center" style={{ color: 'var(--text-muted)' }}>Aucune correspondance</p>
          ) : filtered.map((c, i) => (
            <button key={c.carac_id} type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => { toggle(c); setSearch(''); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
              style={{ borderTop: i > 0 ? '1px solid var(--border)' : 'none',
                color: isAssigned(c.carac_id) ? 'var(--primary-solid)' : 'var(--text)' }}>
              {isAssigned(c.carac_id) ? <X size={12} style={{ color: '#DC2626' }} /> : <Plus size={12} style={{ color: 'var(--primary-solid)' }} />}
              {c.nom}
              <span className="ml-auto text-xs" style={{ color: 'var(--text-disabled)' }}>
                {c.type === 'numeric' && c.unites?.length > 0 ? c.unites.join('/') : c.type}
              </span>
            </button>
          ))}
        </div>
      )}

      {sfId && (
        <button type="button" onClick={save} disabled={saving}
          data-testid="save-sf-caracs-btn"
          className="w-full py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
          style={{ background: 'var(--primary-gradient)' }}>
          {saving ? 'Enregistrement...' : 'Sauvegarder les caractéristiques'}
        </button>
      )}
    </div>
  );
}

export default function Familles() {
  const [familles, setFamilles]         = useState([]);
  const [sousFamilles, setSousFamilles] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedFam, setSelectedFam]   = useState(null);   // famille sélectionnée (pour filtrer sf)
  const [tabType, setTabType]           = useState('tous');  // filtre par type

  // Panels
  const [panelF, setPanelF]   = useState(false);
  const [editF, setEditF]     = useState(null);
  const [formF, setFormF]     = useState(EMPTY_F);
  const [savingF, setSavingF] = useState(false);

  const [panelSF, setPanelSF]   = useState(false);
  const [editSF, setEditSF]     = useState(null);
  const [formSF, setFormSF]     = useState(EMPTY_SF);
  const [savingSF, setSavingSF] = useState(false);
  const [sfCaracs, setSfCaracs] = useState([]);

  // Types TVA pour les sélecteurs
  const [tvaTypes, setTvaTypes] = useState([]);

  // ── Fusion sous-familles ────────────────────────────────────────────────────
  const [mergeModal, setMergeModal]   = useState(null);   // sous-famille source
  const [mergeCibleId, setMergeCibleId] = useState('');
  const [mergeSearch, setMergeSearch] = useState('');
  const [merging, setMerging]         = useState(false);

  const loadF = useCallback(() => {
    setLoading(true);
    Promise.all([api.getFamilles(), api.getSousFamilles(), api.getTvaTypes()])
      .then(([f, sf, tv]) => {
        setFamilles(f.data || []);
        setSousFamilles(sf.data || []);
        setTvaTypes(tv.data || []);
      })
      .catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadF(); }, [loadF]);

  // ── Familles CRUD ────────────────────────────────────────────────────────────
  const openNewF  = () => { setEditF(null); setFormF(EMPTY_F); setPanelF(true); };
  const openEditF = (f) => { setEditF(f); setFormF({ nom: f.nom, type: f.type, compte_charge: f.compte_charge || '', compte_produit: f.compte_produit || '', is_assurance: f.is_assurance || false, tva_vente_type_id: f.tva_vente_type_id || '', tva_achat_type_id: f.tva_achat_type_id || '' }); setPanelF(true); };

  const saveF = async () => {
    if (!formF.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSavingF(true);
    try {
      if (editF) { await api.updateFamille(editF.famille_id, formF); toast.success('Famille modifiée'); }
      else { await api.createFamille(formF); toast.success('Famille créée'); }
      setPanelF(false); loadF();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSavingF(false); }
  };

  const delF = async (f) => {
    if (!window.confirm(`Supprimer la famille "${f.nom}" ?`)) return;
    try { await api.deleteFamille(f.famille_id); toast.success('Famille supprimée'); loadF(); if (selectedFam?.famille_id === f.famille_id) setSelectedFam(null); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  // ── Sous-familles CRUD ───────────────────────────────────────────────────────
  const openNewSF  = (fid) => { setEditSF(null); setFormSF({ ...EMPTY_SF, famille_id: fid || selectedFam?.famille_id || '' }); setSfCaracs([]); setPanelSF(true); };
  const parseCaracs = (v) => { if (!v) return []; if (Array.isArray(v)) return v; try { return JSON.parse(v); } catch { return []; } };
  const openEditSF = (sf) => { setEditSF(sf); setFormSF({ nom: sf.nom, famille_id: sf.famille_id, compte_charge: sf.compte_charge || '', compte_produit: sf.compte_produit || '', tva_vente_type_id: sf.tva_vente_type_id || '', tva_achat_type_id: sf.tva_achat_type_id || '' }); setSfCaracs(parseCaracs(sf.caracteristiques)); setPanelSF(true); };

  const saveSF = async () => {
    if (!formSF.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    if (!formSF.famille_id) { toast.error('Sélectionnez une famille'); return; }
    setSavingSF(true);
    try {
      if (editSF) { await api.updateSousFamille(editSF.sous_famille_id, formSF); toast.success('Sous-famille modifiée'); }
      else { await api.createSousFamille(formSF); toast.success('Sous-famille créée'); }
      setPanelSF(false); loadF();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSavingSF(false); }
  };

  const delSF = async (sf) => {
    if (!window.confirm(`Supprimer la sous-famille "${sf.nom}" ?`)) return;
    try { await api.deleteSousFamille(sf.sous_famille_id); toast.success('Sous-famille supprimée'); loadF(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const openMerge = (sf) => { setMergeModal(sf); setMergeCibleId(''); setMergeSearch(''); };
  const closeMerge = () => { setMergeModal(null); setMergeCibleId(''); setMergeSearch(''); };

  const doMerge = async () => {
    if (!mergeCibleId) { toast.error('Sélectionnez une sous-famille cible'); return; }
    setMerging(true);
    try {
      const res = await api.fusionnerSousFamilles(mergeModal.sous_famille_id, mergeCibleId);
      const { articles_reassignes, source_nom, cible_nom } = res.data;
      toast.success(`"${source_nom}" fusionnée dans "${cible_nom}" — ${articles_reassignes} article(s) réattribué(s)`);
      closeMerge();
      loadF();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur lors de la fusion'); }
    finally { setMerging(false); }
  };

  // ── Computed ─────────────────────────────────────────────────────────────────
  const filteredFamilles = tabType === 'tous' ? familles : familles.filter(f => f.type === tabType);
  const sfForSelected = selectedFam
    ? sousFamilles.filter(sf => sf.famille_id === selectedFam.famille_id)
    : [];
  const sfCount = (fid) => sousFamilles.filter(sf => sf.famille_id === fid).length;

  const inp = "w-full px-3 py-2 border rounded-lg text-sm focus:outline-none";
  const inpSt = { borderColor: 'var(--border)' };
  const lbl = "block text-xs font-semibold uppercase tracking-wider mb-1";
  const lblSt = { color: 'var(--text-muted)' };

  const TypeBadge = ({ type }) => {
    const t = TYPES.find(x => x.value === type);
    if (!t) return null;
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: t.bg, color: t.color }}>{t.label}</span>;
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>Familles & Sous-familles</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Organisez vos articles en familles typées et sous-familles</p>
      </div>

      {/* Tabs type */}
      <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: 'var(--border-light)' }}>
        {[{ key: 'tous', label: 'Toutes' }, ...TYPES].map(tb => (
          <button key={tb.key || tb.value} onClick={() => { setTabType(tb.key || tb.value); setSelectedFam(null); }}
            className="px-4 py-1.5 rounded-lg text-sm font-medium transition-all"
            style={{ background: tabType === (tb.key || tb.value) ? 'white' : 'transparent', color: tabType === (tb.key || tb.value) ? 'var(--primary-solid)' : 'var(--text-muted)', boxShadow: tabType === (tb.key || tb.value) ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>
            {tb.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* ── Familles ──────────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
              <Tag size={13} /> Familles ({filteredFamilles.length})
            </h2>
            <button onClick={openNewF} data-testid="add-famille-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
              style={{ background: 'var(--primary-gradient)' }}>
              <Plus size={12} /> Ajouter
            </button>
          </div>
          {loading ? <div className="text-center py-8 text-sm" style={{ color: 'var(--text-placeholder)' }}>Chargement...</div> : (
            <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
              {filteredFamilles.length === 0 ? (
                <p className="text-sm text-center py-8" style={{ color: 'var(--text-placeholder)' }}>Aucune famille{tabType !== 'tous' ? ' pour ce type' : ''}</p>
              ) : (
                <div>
                  {filteredFamilles.map((f, i) => (
                    <div key={f.famille_id}
                      className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors"
                      onClick={() => setSelectedFam(selectedFam?.famille_id === f.famille_id ? null : f)}
                      style={{ borderTop: i > 0 ? '1px solid #F2F2EE' : 'none', background: selectedFam?.famille_id === f.famille_id ? 'var(--primary-50)' : 'white' }}
                      data-testid={`famille-row-${f.famille_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate" style={{ color: selectedFam?.famille_id === f.famille_id ? 'var(--primary-solid)' : 'var(--text)' }}>{f.nom}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <TypeBadge type={f.type} />
                          <span className="text-xs" style={{ color: 'var(--text-placeholder)' }}>{sfCount(f.famille_id)} sous-famille{sfCount(f.famille_id) > 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={e => { e.stopPropagation(); openEditF(f); }} className="p-1.5 rounded hover:bg-yellow-50" style={{ color: '#B45309' }}><Pencil size={13} /></button>
                        <button onClick={e => { e.stopPropagation(); delF(f); }} className="p-1.5 rounded hover:bg-red-50" style={{ color: '#DC2626' }}><Trash2 size={13} /></button>
                        <ChevronRight size={14} style={{ color: selectedFam?.famille_id === f.famille_id ? 'var(--primary-solid)' : 'var(--text-disabled)', transform: selectedFam?.famille_id === f.famille_id ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Sous-familles ──────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
              <Tag size={13} /> Sous-familles{selectedFam ? ` — ${selectedFam.nom}` : ''}
            </h2>
            {selectedFam && (
              <button onClick={() => openNewSF(selectedFam.famille_id)} data-testid="add-sous-famille-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                style={{ background: 'var(--primary-gradient)' }}>
                <Plus size={12} /> Ajouter
              </button>
            )}
          </div>
          <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            {!selectedFam ? (
              <div className="flex flex-col items-center justify-center py-12">
                <ChevronRight size={32} className="mb-2" style={{ color: 'var(--text-disabled)' }} />
                <p className="text-sm text-center" style={{ color: 'var(--text-placeholder)' }}>Sélectionnez une famille<br />pour voir ses sous-familles</p>
              </div>
            ) : sfForSelected.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10">
                <p className="text-sm text-center mb-3" style={{ color: 'var(--text-placeholder)' }}>Aucune sous-famille</p>
                <button onClick={() => openNewSF(selectedFam.famille_id)}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-medium"
                  style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                  <Plus size={11} /> Créer la première
                </button>
              </div>
            ) : (
              <div>
                {sfForSelected.map((sf, i) => (
                  <div key={sf.sous_famille_id}
                    className="flex items-center justify-between px-4 py-3"
                    style={{ borderTop: i > 0 ? '1px solid #F2F2EE' : 'none' }}
                    data-testid={`sous-famille-row-${sf.sous_famille_id}`}>
                    <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{sf.nom}</span>
                    <div className="flex gap-1">
                      <button onClick={() => openMerge(sf)} title="Fusionner avec une autre sous-famille"
                        className="p-1.5 rounded hover:bg-blue-50" style={{ color: '#2563EB' }}
                        data-testid={`sous-famille-merge-${sf.sous_famille_id}`}>
                        <Merge size={13} />
                      </button>
                      <button onClick={() => openEditSF(sf)} className="p-1.5 rounded hover:bg-yellow-50" style={{ color: '#B45309' }}><Pencil size={13} /></button>
                      <button onClick={() => delSF(sf)} className="p-1.5 rounded hover:bg-red-50" style={{ color: '#DC2626' }}><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Panel Famille ──────────────────────────────────────────────────── */}
      <SidePanel open={panelF} onOpenChange={v => { if (!v) setPanelF(false); }}
        title={editF ? 'Modifier la famille' : 'Nouvelle famille'}
        footer={
          <div className="flex gap-3 justify-end">
            <button onClick={() => setPanelF(false)} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--text-muted)' }}>Annuler</button>
            <button onClick={saveF} disabled={savingF} data-testid="save-famille-btn"
              className="px-5 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ background: 'var(--primary-gradient)' }}>
              {savingF ? 'Enregistrement...' : (editF ? 'Modifier' : 'Créer')}
            </button>
          </div>
        }>
        <div className="space-y-4">
          <div>
            <label className={lbl} style={lblSt}>Nom *</label>
            <input value={formF.nom} onChange={e => setFormF(f => ({ ...f, nom: e.target.value }))}
              placeholder="ex: Moteur, Hydraulique, Entretien"
              data-testid="famille-nom-input" className={inp} style={inpSt} />
          </div>
          <div>
            <label className={lbl} style={lblSt}>Type d'article *</label>
            <div className="grid grid-cols-3 gap-2">
              {TYPES.map(t => (
                <button key={t.value} type="button"
                  onClick={() => setFormF(f => ({ ...f, type: t.value }))}
                  className="py-2 rounded-lg text-xs font-medium border transition-all"
                  style={{ background: formF.type === t.value ? t.bg : 'white', color: formF.type === t.value ? t.color : 'var(--text-muted)', borderColor: formF.type === t.value ? t.color : 'var(--border)', fontWeight: formF.type === t.value ? 600 : 400 }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={lbl} style={lblSt}>Compte de charge (classe 6)</label>
            <input value={formF.compte_charge || ''} onChange={e => setFormF(f => ({ ...f, compte_charge: e.target.value }))}
              placeholder="ex: 601000, 606100"
              data-testid="famille-compte-charge-input" className={inp} style={inpSt} />
            <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>
              S'applique à toutes les sous-familles sans compte propre
            </p>
          </div>
          <div>
            <label className={lbl} style={lblSt}>Compte de produit (classe 7)</label>
            <input value={formF.compte_produit || ''} onChange={e => setFormF(f => ({ ...f, compte_produit: e.target.value }))}
              placeholder="ex: 706000, 707000"
              data-testid="famille-compte-produit-input" className={inp} style={inpSt} />
            <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>
              Compte de vente / produit associé à cette famille
            </p>
          </div>
          {/* Toggle is_assurance — uniquement pour les familles de type Location */}
          {formF.type === 'location' && (
            <div className="flex items-center justify-between px-4 py-3 rounded-xl border" style={{ borderColor: 'var(--border)', background: '#F0F9FF' }}>
              <div>
                <p className="text-sm font-semibold" style={{ color: '#0369A1' }}>Famille Assurance</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  Identifie cette famille comme la famille d'articles d'assurance
                </p>
              </div>
              <button type="button"
                onClick={() => setFormF(f => ({ ...f, is_assurance: !f.is_assurance }))}
                data-testid="famille-is-assurance-toggle"
                className="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors"
                style={{ background: formF.is_assurance ? '#0369A1' : 'var(--border)' }}>
                <span className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
                  style={{ transform: formF.is_assurance ? 'translateX(16px)' : 'translateX(0)' }} />
              </button>
            </div>
          )}

          {/* TVA par défaut */}
          {tvaTypes.length > 0 && (
            <div className="pt-3 border-t space-y-3" style={{ borderColor: 'var(--border)' }}>
              <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>TVA par défaut</p>
              <div>
                <label className={lbl} style={lblSt}>TVA Vente</label>
                <select value={formF.tva_vente_type_id || ''} onChange={e => setFormF(f => ({ ...f, tva_vente_type_id: e.target.value }))}
                  data-testid="famille-tva-vente-select" className={inp} style={inpSt}>
                  <option value="">— Aucune —</option>
                  {tvaTypes.map(t => <option key={t.tva_type_id} value={t.tva_type_id}>{t.designation}</option>)}
                </select>
                <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>Pré-remplit le taux dans les devis (modifiable)</p>
              </div>
              <div>
                <label className={lbl} style={lblSt}>TVA Achat</label>
                <select value={formF.tva_achat_type_id || ''} onChange={e => setFormF(f => ({ ...f, tva_achat_type_id: e.target.value }))}
                  data-testid="famille-tva-achat-select" className={inp} style={inpSt}>
                  <option value="">— Aucune —</option>
                  {tvaTypes.map(t => <option key={t.tva_type_id} value={t.tva_type_id}>{t.designation}</option>)}
                </select>
                <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>Pré-remplit le taux dans les factures d'achat (modifiable)</p>
              </div>
            </div>
          )}
        </div>
      </SidePanel>

      {/* ── Panel Sous-famille ─────────────────────────────────────────────── */}
      <SidePanel open={panelSF} onOpenChange={v => { if (!v) setPanelSF(false); }}
        title={editSF ? 'Modifier la sous-famille' : 'Nouvelle sous-famille'}
        footer={
          <div className="flex gap-3 justify-end">
            <button onClick={() => setPanelSF(false)} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--text-muted)' }}>Annuler</button>
            <button onClick={saveSF} disabled={savingSF} data-testid="save-sous-famille-btn"
              className="px-5 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ background: 'var(--primary-gradient)' }}>
              {savingSF ? 'Enregistrement...' : (editSF ? 'Modifier' : 'Créer')}
            </button>
          </div>
        }>
        <div className="space-y-4">
          <div>
            <label className={lbl} style={lblSt}>Famille parente *</label>
            <select value={formSF.famille_id} onChange={e => setFormSF(f => ({ ...f, famille_id: e.target.value }))}
              data-testid="sous-famille-famille-select" className={inp} style={inpSt}>
              <option value="">— Sélectionner une famille —</option>
              {familles.map(f => <option key={f.famille_id} value={f.famille_id}>{f.nom} ({TYPES.find(t => t.value === f.type)?.label || f.type})</option>)}
            </select>
          </div>
          <div>
            <label className={lbl} style={lblSt}>Nom *</label>
            <input value={formSF.nom} onChange={e => setFormSF(f => ({ ...f, nom: e.target.value }))}
              placeholder="ex: Distribution, Filtration"
              data-testid="sous-famille-nom-input" className={inp} style={inpSt} />
          </div>
          <div>
            <label className={lbl} style={lblSt}>Compte de charge (classe 6)</label>
            <input value={formSF.compte_charge || ''} onChange={e => setFormSF(f => ({ ...f, compte_charge: e.target.value }))}
              placeholder="ex: 601000 (laissez vide = hérite de la famille)"
              data-testid="sous-famille-compte-charge-input" className={inp} style={inpSt} />
            <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>
              Optionnel — prioritaire sur le compte de la famille
            </p>
          </div>
          <div>
            <label className={lbl} style={lblSt}>Compte de produit (classe 7)</label>
            <input value={formSF.compte_produit || ''} onChange={e => setFormSF(f => ({ ...f, compte_produit: e.target.value }))}
              placeholder="ex: 706000 (laissez vide = hérite de la famille)"
              data-testid="sous-famille-compte-produit-input" className={inp} style={inpSt} />
            <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>
              Optionnel — prioritaire sur le compte de la famille
            </p>
          </div>
          {/* Caractéristiques techniques */}
          <SousFamilleCaracsSection
            sfId={editSF?.sous_famille_id || null}
            sfCaracs={sfCaracs}
            onSaved={(updated) => setSfCaracs(updated)}
          />

          {/* TVA par défaut */}
          {tvaTypes.length > 0 && (
            <div className="pt-3 border-t space-y-3" style={{ borderColor: 'var(--border)' }}>
              <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>TVA par défaut</p>
              <div>
                <label className={lbl} style={lblSt}>TVA Vente</label>
                <select value={formSF.tva_vente_type_id || ''} onChange={e => setFormSF(f => ({ ...f, tva_vente_type_id: e.target.value }))}
                  data-testid="sous-famille-tva-vente-select" className={inp} style={inpSt}>
                  <option value="">— Aucune (hérite de la famille) —</option>
                  {tvaTypes.map(t => <option key={t.tva_type_id} value={t.tva_type_id}>{t.designation}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl} style={lblSt}>TVA Achat</label>
                <select value={formSF.tva_achat_type_id || ''} onChange={e => setFormSF(f => ({ ...f, tva_achat_type_id: e.target.value }))}
                  data-testid="sous-famille-tva-achat-select" className={inp} style={inpSt}>
                  <option value="">— Aucune (hérite de la famille) —</option>
                  {tvaTypes.map(t => <option key={t.tva_type_id} value={t.tva_type_id}>{t.designation}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      </SidePanel>

      {/* ── Modale Fusion sous-familles ───────────────────────────────────── */}
      {mergeModal && (() => {
        const candidates = sousFamilles.filter(sf => sf.sous_famille_id !== mergeModal.sous_famille_id);
        const filtered = mergeSearch
          ? candidates.filter(sf => sf.nom.toLowerCase().includes(mergeSearch.toLowerCase()))
          : candidates;
        const cibleSf = candidates.find(sf => sf.sous_famille_id === mergeCibleId);
        const famOf = (sf) => familles.find(f => f.famille_id === sf.famille_id);

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.45)' }}
            onClick={e => { if (e.target === e.currentTarget) closeMerge(); }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              style={{ border: '1px solid var(--border)' }}
              data-testid="merge-sf-modal">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b"
                style={{ borderColor: 'var(--border)', background: 'var(--primary-50)' }}>
                <div className="flex items-center gap-2">
                  <Merge size={16} style={{ color: 'var(--primary-solid)' }} />
                  <span className="font-bold text-sm" style={{ color: 'var(--primary-solid)',  }}>
                    Fusionner une sous-famille
                  </span>
                </div>
                <button onClick={closeMerge} className="p-1.5 rounded hover:bg-white/80" style={{ color: 'var(--text-muted)' }}>
                  <X size={14} />
                </button>
              </div>

              <div className="p-5 space-y-4">
                {/* Source */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>
                    Source (sera supprimée)
                  </p>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border"
                    style={{ borderColor: '#DC2626', background: '#FEF2F2' }}>
                    <span className="text-sm font-semibold" style={{ color: '#DC2626' }}>{mergeModal.nom}</span>
                    {famOf(mergeModal) && (
                      <span className="text-xs px-2 py-0.5 rounded-full ml-auto"
                        style={{ background: 'var(--border-light)', color: 'var(--text-muted)' }}>
                        {famOf(mergeModal).nom}
                      </span>
                    )}
                  </div>
                </div>

                {/* Cible */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>
                    Cible (conservée)
                  </p>
                  {/* Recherche */}
                  <div className="relative mb-2">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                    <input value={mergeSearch} onChange={e => setMergeSearch(e.target.value)}
                      placeholder="Rechercher une sous-famille..."
                      data-testid="merge-sf-search"
                      className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm focus:outline-none"
                      style={{ borderColor: 'var(--border)' }} />
                  </div>
                  {/* Liste */}
                  <div className="rounded-lg border overflow-hidden max-h-44 overflow-y-auto"
                    style={{ borderColor: 'var(--border)' }}>
                    {filtered.length === 0 ? (
                      <p className="px-3 py-3 text-xs text-center" style={{ color: 'var(--text-placeholder)' }}>
                        Aucune sous-famille trouvée
                      </p>
                    ) : filtered.map((sf, i) => (
                      <button key={sf.sous_famille_id} type="button"
                        onClick={() => setMergeCibleId(sf.sous_famille_id)}
                        data-testid={`merge-cible-${sf.sous_famille_id}`}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors"
                        style={{
                          borderTop: i > 0 ? '1px solid #F2F2EE' : 'none',
                          background: mergeCibleId === sf.sous_famille_id ? 'var(--primary-50)' : 'white',
                          color: mergeCibleId === sf.sous_famille_id ? 'var(--primary-solid)' : 'var(--text)',
                        }}>
                        <span className="flex-1 font-medium">{sf.nom}</span>
                        {famOf(sf) && (
                          <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                            style={{ background: 'var(--border-light)', color: 'var(--text-muted)' }}>
                            {famOf(sf).nom}
                          </span>
                        )}
                        {mergeCibleId === sf.sous_famille_id && (
                          <span className="text-xs font-bold flex-shrink-0" style={{ color: 'var(--primary-solid)' }}>✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Résumé */}
                {cibleSf && (
                  <div className="px-3 py-2.5 rounded-lg text-sm" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                    <span style={{ color: '#166534' }}>
                      Tous les articles de <strong>"{mergeModal.nom}"</strong> seront réattribués à <strong>"{cibleSf.nom}"</strong>.
                      Les caractéristiques seront fusionnées. Cette action est <strong>irréversible</strong>.
                    </span>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex gap-3 justify-end px-5 py-4 border-t" style={{ borderColor: 'var(--border)' }}>
                <button onClick={closeMerge} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--text-muted)' }}>
                  Annuler
                </button>
                <button onClick={doMerge} disabled={!mergeCibleId || merging}
                  data-testid="confirm-merge-btn"
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                  style={{ background: mergeCibleId ? '#DC2626' : 'var(--border)' }}>
                  <Merge size={13} />
                  {merging ? 'Fusion en cours...' : 'Fusionner & supprimer la source'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
